package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import kotlin.math.roundToLong
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.SettingsToggleRow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCadCents
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_back
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_next
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_add_button
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_add_section
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_added_section
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_later_note
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_name
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_price
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_promoted_badge
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_promoted_desc
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_promoted_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_remove_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_skip
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_products_title
import org.jetbrains.compose.resources.stringResource

/**
 * Step 4 — Products & promotions, fully OPTIONAL (many listings — a lawyer, a dentist — have
 * none, so the primary action reads "Skip" until something is added). Owners can seed what they
 * sell and flag one or more items as the promotional product (Business Detail's Promotions rail);
 * everything stays editable later via Dashboard → Manage products, which this form mirrors.
 * No Stitch frame exists for this step (owner-requested 2026-07-18) — it reuses the wizard's
 * card/heading/toggle patterns so it reads as part of the same flow.
 */
@Composable
fun AddBusinessProductsScreen(
    draft: AddBusinessDraft,
    onAddProduct: (name: String, priceCents: Long, promoted: Boolean) -> Unit,
    onRemoveProduct: (Int) -> Unit,
    onBack: () -> Unit,
    onNext: () -> Unit,
    modifier: Modifier = Modifier,
) {
    WizardScaffold(
        stepIndex = 4,
        title = stringResource(Res.string.ab_products_title),
        onBack = onBack,
        modifier = modifier,
        bottomBar = {
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                AppButton(
                    text = stringResource(Res.string.ab_back),
                    onClick = onBack,
                    modifier = Modifier.weight(1f),
                    variant = AppButtonVariant.Secondary,
                )
                AppButton(
                    text = stringResource(
                        if (draft.products.isEmpty()) Res.string.ab_products_skip else Res.string.ab_next,
                    ),
                    onClick = onNext,
                    modifier = Modifier.weight(1f),
                    trailingIcon = "arrow_forward",
                )
            }
        },
    ) {
        Text(
            text = stringResource(Res.string.ab_products_subtitle),
            style = AppTheme.typography.bodySm,
            color = AppTheme.colors.onSurfaceVariant,
        )

        // Add-product form
        var name by remember { mutableStateOf("") }
        var price by remember { mutableStateOf("") }
        var promoted by remember { mutableStateOf(false) }
        WizardCard {
            SectionHeading(stringResource(Res.string.ab_products_add_section))
            AppTextField(
                value = name,
                onValueChange = { name = it },
                label = stringResource(Res.string.ab_products_name),
                modifier = Modifier.fillMaxWidth(),
            )
            AppTextField(
                value = price,
                onValueChange = { price = it.filter { c -> c.isDigit() || c == '.' } },
                label = stringResource(Res.string.ab_products_price),
                modifier = Modifier.fillMaxWidth(),
            )
            SettingsToggleRow(
                iconName = "local_offer",
                title = stringResource(Res.string.ab_products_promoted_title),
                subtitle = stringResource(Res.string.ab_products_promoted_desc),
                checked = promoted,
                onCheckedChange = { promoted = it },
            )
            // Round, don't truncate: 19.99 * 100 is 1998.9999… in binary float, so toLong() gave 1998
            // ($19.98). roundToLong() lands on the correct 1999 cents.
            val priceCents = price.toDoubleOrNull()?.let { (it * 100).roundToLong() } ?: 0L
            AppButton(
                text = stringResource(Res.string.ab_products_add_button),
                onClick = {
                    onAddProduct(name, priceCents, promoted)
                    name = ""; price = ""; promoted = false
                },
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = "add",
                enabled = name.isNotBlank() && priceCents > 0,
            )
        }

        // Drafted products
        if (draft.products.isNotEmpty()) {
            WizardCard {
                SectionHeading(stringResource(Res.string.ab_products_added_section))
                draft.products.forEachIndexed { index, product ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                    ) {
                        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
                            Text(product.name, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
                            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm), verticalAlignment = Alignment.CenterVertically) {
                                Text(product.priceCents.formatCadCents(), style = AppTheme.typography.bodySm, color = AppTheme.colors.primary)
                                if (product.promoted) {
                                    Text(
                                        text = stringResource(Res.string.ab_products_promoted_badge),
                                        style = AppTheme.typography.labelSm,
                                        color = AppTheme.colors.secondary,
                                    )
                                }
                            }
                        }
                        AppIconButton(
                            iconName = "delete",
                            contentDescription = stringResource(Res.string.ab_products_remove_a11y),
                            onClick = { onRemoveProduct(index) },
                        )
                    }
                }
            }
        }

        InfoNote(stringResource(Res.string.ab_products_later_note))
    }
}
